import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { ShoppingCart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MerchPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/merch/products`);
      setProducts(response.data.products);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLowestPrice = (product) => {
    if (!product.variants || product.variants.length === 0) return 0;
    const prices = product.variants.map(v => v.price);
    return Math.min(...prices) / 100;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="mb-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <ShoppingCart className="w-12 h-12 text-red-600" />
            <h1 
              data-testid="merch-heading"
              className="text-4xl sm:text-5xl font-bold"
              style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
            >
              Official Merch & Music
            </h1>
          </div>
          <p className="text-xl text-slate-400" style={{ fontFamily: 'Inter, sans-serif' }}>
            Support Unfiltered Vision News with official merchandise and music
          </p>
        </div>

        {/* Featured Music Section */}
        <div className="mb-16 bg-gradient-to-r from-slate-900 to-slate-800 p-8 rounded-sm border border-slate-700">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-full md:w-1/3">
              <img 
                src="https://distrokid.imgix.net/http%3A%2F%2Fgather.fandalism.com%2F10059303--802CAC76-2CB3-4527-8F5CDEF37071FF6A--0--211746--DIVINEECHO202507271822220000.jpg?fm=jpg&q=75&w=800&s=44ddd6629a07652cfe366264ad9fa5c6"
                alt="Heaven Touch Earth"
                className="w-full rounded-sm shadow-lg"
              />
            </div>
            <div className="w-full md:w-2/3">
              <div className="inline-block mb-3 px-3 py-1 bg-red-600 text-white text-sm font-bold rounded-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                NEW MUSIC
              </div>
              <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Heaven Touch Earth
              </h2>
              <p className="text-xl text-slate-300 mb-2" style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic' }}>
                by Divine Echo Collective
              </p>
              <p className="text-slate-400 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                Soul-stirring gospel music that blends worship, hope, and praise. Every song is a prayer and a declaration, inspiring faith and drawing listeners closer to God.
              </p>
              <a 
                href="https://distrokid.com/hyperfollow/divineechocollective/heaven-touch-earth"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="stream-music-button"
              >
                <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg rounded-sm font-bold" style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>
                  Stream Now on All Platforms
                </Button>
              </a>
              <p className="text-sm text-slate-500 mt-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                Available on Spotify, Apple Music, YouTube Music, iHeartRadio, Deezer & more
              </p>
            </div>
          </div>
        </div>

        {/* Merchandise Section */}
        <div className="mb-8 flex items-center justify-between">
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>
            Official Merchandise
          </h2>
          <a 
            href="https://unfilteredvisionnews.printify.me/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="bg-red-600 hover:bg-red-700 text-white px-8" style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>
              Shop All Products
            </Button>
          </a>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-slate-800 aspect-square rounded-sm mb-4"></div>
                <div className="h-4 bg-slate-800 rounded mb-2"></div>
                <div className="h-3 bg-slate-800 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-xl text-slate-400 mb-8">No products available yet. Check back soon!</p>
            <Link to="/">
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                Return Home
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {products.map((product) => (
              <a 
                key={product.id} 
                href="https://unfilteredvisionnews.printify.me/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`product-card-${product.id}`}
                className="group"
              >
                <div className="relative aspect-square rounded-sm overflow-hidden mb-4 border border-slate-800 hover:border-red-600 bg-slate-900" style={{ transition: 'border 0.3s' }}>
                  {product.images && product.images.length > 0 ? (
                    <img 
                      src={product.images[0].src} 
                      alt={product.title}
                      className="w-full h-full object-cover group-hover:scale-105" 
                      style={{ transition: 'transform 0.5s' }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-600">
                      <ShoppingCart className="w-16 h-16" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center" style={{ transition: 'opacity 0.3s' }}>
                    <Button className="bg-red-600 hover:bg-red-700 text-white">
                      Shop Now
                    </Button>
                  </div>
                </div>
                <h3 
                  className="text-lg font-bold mb-2 line-clamp-2 group-hover:text-red-600"
                  style={{ fontFamily: 'Oswald, sans-serif', transition: 'color 0.3s' }}
                >
                  {product.title}
                </h3>
                <p className="text-lg font-bold text-red-500" style={{ fontFamily: 'Inter, sans-serif' }}>
                  From ${getLowestPrice(product).toFixed(2)}
                </p>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchPage;
